<template>
  <div>
    <div v-if="$redirectToPatientAppFlag" class="max-width">
      <Alert>
        <a :href="PATIENT_APP_URL" class="link">
          The Ciitizen patient portal has moved to a new address. <u>Click here</u> to be redirected
          to log in. <br />Or access our login at any time from the ciitizen website at
          www.ciitizen.com.
        </a>
      </Alert>
    </div>
    <div v-else class="login-wrapper">
      <div class="right-side-wrapper">
        <div class="right-side">
          <v-form ref="form" class="form-container" @submit.prevent="onSubmit">
            <v-text-field
              id="email"
              v-model="formData.email"
              label="User Name"
              required
              :rules="emailRules"
              style="margin-bottom: 10px"
            />
            <v-text-field
              id="password"
              v-model="formData.password"
              type="password"
              label="Password"
            />
            <v-btn
              ref="btn-submit"
              depressed
              :disabled="!(formData.password.length && formData.email.length)"
              color="#128279"
              class="btn-styling"
              shaped
              height="40px"
              max-height="44px"
              type="submit"
            >
              Login
            </v-btn>
            <div class="forget-password" @click="movetoForget">Forget Password?</div>
          </v-form>
        </div>
        <div class="sign-up-wrapper">
          <div class="no-account-styling" id="no-account">Don’t have an account?</div>
          <div class="sign-up-styling">
            <a @click="onboardingClick" target="_blank" style="text-decoration: none" id="sign-up"
              >Sign up
            </a>
          </div>
        </div>
      </div>
    </div>
    <snack-bar
      id="snack-bar"
      :snackbar="snackbar"
      :snackbarMsg="errorMessage"
      iconName="fa-regular fa-xmark"
      :isSuccess="isSuccess"
    />
    <progress-loader v-if="loading" id="progress-loader" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { onBoardingLink, onboardingUrl, DEFAULT_INVITE_TOKEN, PATIENT_APP_URL } from 'config'
import { Utils } from '@ciitizen/component-library'
import Alert from '../components/alert.vue'

export default {
  name: 'Login',
  data() {
    return {
      formData: {
        email: '',
        password: ''
      },
      timeout: 3000,
      loading: false,
      emailRules: [
        v => !!v || 'User Name is required',
        v => /.+@.+/.test(v) || 'User Name must be valid'
      ],
      errorMessage: '',
      snackbar: false,
      onboardingLink: onBoardingLink,
      isSuccess: false,
      PATIENT_APP_URL
    }
  },
  computed: {
    ...mapState({
      userId: state => state.login.userId
    }),
    year() {
      const date = new Date()
      return date.getFullYear()
    }
  },
  methods: {
    ...mapActions(['login', 'fetchOnboardingSession', 'resumeOnboarding']),
    onboardingClick() {
      if (window.location.href.includes('hie-access')) {
        window.location.href = `${onboardingUrl}/hie/cures_gateway`
      } else if (this.$redirectToPatientAppFlag) {
        window.location.href = PATIENT_APP_URL
      } else if (this.$modularOnboardingEnabled) {
        this.$router.push({ name: 'register', query: { tkn: DEFAULT_INVITE_TOKEN } })
      } else {
        window.location.href = this.onboardingLink
      }
    },
    async onSubmit() {
      if (!this.$refs.form.validate()) return
      try {
        Utils.callSegmentTrack({ name: 'pp.username_password.submitted' })
        this.loading = true
        let payload = {
          username: this.formData.email.toLowerCase().trim(),
          password: this.formData.password
        }
        await this.login(payload)
        Utils.callSegmentTrack({ name: 'pp.login.success' })
        this.loading = false
        this.hasOnboardingCompleted()
      } catch (err) {
        Utils.callSegmentTrack({
          name: 'pp.login_error.fired',
          err_text: this.getErrMsgText(err)
        })
        this.loading = false
        this.isSuccess = false
        this.snackbar = true
        this.dismissSnakeBar()
        this.getErrMsgText(err)
        throw err
      }
    },
    getErrMsgText(res) {
      this.errorMessage = 'Invalid Username or Password'
      if (res && res.data) {
        if (res.data.message) {
          this.errorMessage = res.data.message
        } else if (res.data.code) {
          switch (res.data.code) {
            case '403':
              this.errorMessage = 'Invalid Username or Password' // we can have custom msgs later
              break
          }
        }
      }
      return this.errorMessage
    },
    dismissSnakeBar() {
      setTimeout(() => {
        this.snackbar = false
      }, this.timeout)
    },
    movetoSummary() {
      localStorage.removeItem('isAdminToken') // clear admin token incase of normal user login
      this.$router.push(`/${this.userId}/home`)
    },
    movetoForget() {
      Utils.callSegmentTrack({ name: 'pp.forget_password.clicked' })
      this.$router.push({
        name: 'forgetPassword'
      })
    },
    async hasOnboardingCompleted() {
      if (this.userId) {
        const hasSession = await this.fetchOnboardingSession(this.userId)
        hasSession ? this.resumeOnboarding(hasSession) : this.movetoSummary()
      }
    },
    redirectToAnotherPage() {
      setTimeout(() => {
        window.location.href = PATIENT_APP_URL
      }, 10000)
    }
  },
  mounted() {
    Utils.callSegmentTrack({ name: 'pp.login_page.landed' })
    if (this.$redirectToPatientAppFlag && !window.location.href.includes('hie-access')) {
      this.redirectToAnotherPage()
    }
  },
  components: {
    Alert
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="stylus" scoped>
.login-wrapper
  display: flex
  height: 90%
  justify-content: center
  align-items: center

.right-side
  width: 100%

.btn-styling
  color: white
  font-weight: 700
  outline: none !important
  margin-top: 30px
  text-transform: capitalize
  font-size: 18px

.sign-up-wrapper
  display: flex
  margin-top: 30px
  justify-content: center

.sign-up-styling
  font-style: normal
  font-weight: 400
  font-size: 16px
  line-height: 19px
  color: #185ABC
  margin-left: 5px

.no-account-styling
  font-style: normal
  font-weight: normal
  font-size: 16px
  line-height: 19px
  color: #4F4F4F

.right-side-wrapper
  display: block
  width: 300px

.form-container
  display: flex
  flex-direction: column

.hyperlink-style
  text-decoration: none
  color: #80868B
.forget-password
  font-style: normal
  font-weight: 400
  font-size: 16px
  line-height: 17px
  color: #828282
  margin-top: 15px
  display:flex
  width : 100%
  justify-content: center
  cursor: pointer

/* Handling medium sized screens */
@media only screen and (max-width: 769px)
  .login-wrapper
    padding: 0 40px
  .right-side-wrapper
    display: block

/* For mobile screens */
@media only screen and (max-width: 561px)
  .login-wrapper
    display: flex
    flex-direction: column
    justify-content: flex-start
    padding: 24px
  .btn-styling
    width: 100%
  .right-side
    width: 325px
    padding-top: 40px
  .right-side-wrapper
    display: contents

.max-width
  width: 325px
</style>
